import type { FC } from 'react'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Typography,
  Button,
  Modal,
  IconButton,
  TextField,
  Menu,
  MenuItem,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import InsertCommentOutlinedIcon from '@mui/icons-material/InsertCommentOutlined'
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined'
import { Loader } from '~/shared/ui'
import { providersUserPersonPersonPreference } from '@fireflyhealth/api'
import {
  PersonPreferenceCreateUpdateInterface,
  useCreatePersonPreference,
  useFetchPersonPreference,
  useUpdatePersonPreference,
} from '~/entities/steerage'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { required } from '~/shared/lib/required'

interface PersonPreferenceProps {
  personId: number
  showTitle: boolean
}

const MODAL_TITLE_SX = { fontWeight: 500, fontSize: '20px', lineHeight: '16px' }

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 514,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
}

const PersonPreference: FC<PersonPreferenceProps> = props => {
  const client = useQueryClient()
  const [preference, setPreference] = useState<string>('')
  //API for fetching person preference
  const { isPending: isFetchingPersonPreference, data: personPreferences } =
    useFetchPersonPreference(props?.personId)
  // API for creating person preference
  const { isPending: isAddingPersonPreference, mutate: handleCreatePersonPreference } =
    useCreatePersonPreference()
  // API for editing person preference
  const { isPending: isUpdatingPersonPreference, mutate: handleUpdatePersonPreference } =
    useUpdatePersonPreference()
  const [personPreference] = personPreferences ?? []

  // Handling the Create/Update Person Preference Modal
  const [open, setOpen] = useState(false)
  const handleCreateUpdateModalOpen = () => {
    // Close the menu before opening the modal. Both install their own focus
    // trap on `document`; leaving the menu open keeps two traps active at once,
    // and they fight each other for focus indefinitely.
    handleMenuClose()
    setOpen(true)
  }
  const handleCreateUpdateModalClose = () => {
    setOpen(false)
    setPreference(personPreference?.preference ?? '')
    // close the menu in case open
    handleMenuClose()
  }

  // Handling the delete Person Preference Modal
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const handleDeleteModalOpen = () => {
    // Close the menu before opening the modal so the menu's focus trap does not
    // contend with the modal's (see handleCreateUpdateModalOpen).
    handleMenuClose()
    setOpenDeleteModal(true)
  }
  const handleDeleteModalClose = () => {
    setOpenDeleteModal(false)
    // Close the menu
    handleMenuClose()
  }

  // The editable text resets to the saved preference whenever that reloads.
  const [loadedPersonPreference, setLoadedPersonPreference] = useState<typeof personPreference>()
  if (personPreference !== loadedPersonPreference) {
    setLoadedPersonPreference(personPreference)
    setPreference(personPreference?.preference ?? '')
  }

  // The mutations report their own failures; this refetch is separate work, and a per-call
  // `onSuccess` is typed to return void so nothing would await it.
  const refreshPreferences = () =>
    fireAndForget('[Steerage] refresh member preferences', async () => {
      const queryKey = providersUserPersonPersonPreference.listQueryKey({
        person_id: props.personId,
        provider_id: 'me',
      })
      await client.cancelQueries({ queryKey })
      await client.invalidateQueries({ queryKey })
    })

  const handleCreateUpdateModal = () => {
    // First create payload
    const payload: PersonPreferenceCreateUpdateInterface = {
      person: props?.personId,
      preference: preference,
    }

    // Check if we are creating person preference
    if (!personPreference) {
      // Call the API to create person preferences
      handleCreatePersonPreference(payload, { onSuccess: refreshPreferences })
      // Close the modal
      handleCreateUpdateModalClose()
      return
    }

    // Else Update the person preference
    payload['personPreferenceId'] = personPreference.id
    // Call the API to update person preferences
    handleUpdatePersonPreference(payload, { onSuccess: refreshPreferences })
    // Close the modal
    handleCreateUpdateModalClose()
    // close the menu
    handleMenuClose()
    return
  }

  const handleDeletePersonPreferenceModal = () => {
    // First create payload
    const payload: PersonPreferenceCreateUpdateInterface = {
      person: props?.personId,
      preference: null,
      personPreferenceId: required(personPreference, 'a preference to delete').id,
    }

    // Call the API to update person preferences with preference as null
    handleUpdatePersonPreference(payload, { onSuccess: refreshPreferences })
    // Close the modal
    handleDeleteModalClose()
    // Close the menu
    handleMenuClose()
    return
  }

  // for opening and closing menu icon
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const openMenu = Boolean(anchorEl)
  const handleMenuClose = () => {
    setAnchorEl(null)
  }
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  return (
    <Box>
      {isFetchingPersonPreference || isAddingPersonPreference || isUpdatingPersonPreference ? (
        <Loader />
      ) : null}
      {props?.showTitle && (
        <Typography variant="body1" style={{ fontWeight: 500 }}>
          Member Preferences
        </Typography>
      )}
      {personPreference?.preference ? (
        <Alert
          iconMapping={{
            info: <InsertCommentOutlinedIcon fontSize="inherit" />,
          }}
          action={
            <Box sx={{ mt: -1 }}>
              <IconButton onClick={handleMenuClick}>
                <MoreVertOutlinedIcon />
              </IconButton>
            </Box>
          }
          severity="info"
        >
          {personPreference?.preference}
        </Alert>
      ) : (
        <Box sx={{ ml: -1, mt: 1, mb: 1 }}>
          <Alert severity="info" sx={{ alignItems: 'center', p: -2 }}>
            <Button onClick={handleCreateUpdateModalOpen}>
              + Add member preferences (applicable for all requests)
            </Button>
          </Alert>
        </Box>
      )}
      <Modal open={open} onClose={handleCreateUpdateModalClose}>
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography sx={MODAL_TITLE_SX}>Update member preferences</Typography>
            <Box sx={{ mt: -1 }}>
              <IconButton size="small" onClick={handleCreateUpdateModalClose}>
                <ClearIcon />
              </IconButton>
            </Box>
          </Box>
          <TextField
            id="standard-multiline-flexible"
            label="Navigation Preferences"
            multiline
            variant="standard"
            helperText="e.g. addresses other than home, provider gender, last mile locations"
            value={preference}
            onChange={e => setPreference(e.target.value)}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 300 } }}
          />
          <Box sx={{ mt: 5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleCreateUpdateModalClose}>
              <Typography sx={{ fontWeight: 600, mr: '10px' }}>Cancel</Typography>
            </Button>
            <Button
              onClick={handleCreateUpdateModal}
              variant="contained"
              color="primary"
              sx={{ borderRadius: '28px' }}
            >
              <Typography>Save and Update Preferences</Typography>
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal open={openDeleteModal} onClose={handleDeleteModalClose}>
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography sx={MODAL_TITLE_SX}>Delete Member Preference?</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography sx={{ fontWeight: 400 }}>
              This will completely remove all logged preferences from the member’s profile.{' '}
            </Typography>
          </Box>
          <Box sx={{ mt: 3, mb: -2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleDeleteModalClose}>
              <Typography sx={{ fontWeight: 500 }}>CANCEL</Typography>
            </Button>
            <Button onClick={handleDeletePersonPreferenceModal}>
              <Typography sx={{ fontWeight: 500 }}>DELETE PREFERENCE</Typography>
            </Button>
          </Box>
        </Box>
      </Modal>
      <Menu
        elevation={1}
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        slotProps={{
          list: {
            disablePadding: true,
          },
        }}
      >
        <MenuItem onClick={handleCreateUpdateModalOpen}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteModalOpen}>Delete</MenuItem>
      </Menu>
    </Box>
  )
}

export default PersonPreference
